(ns app.front.view.party.search
  (:require [app.domain.party.model :as party-model]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.util :refer [for-frag]]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.string :as str-util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-fx
  ::init
  (fn [{:keys [db]} [_ party-id]]
    {:db           (merge db {::get-integration-contact-result nil
                              ::integration-contacts-loading   true})
     :comm/command {:command    :party/search-integration-contacts
                    :method     :get
                    :data       {:party-id party-id}
                    :on-success [::store-integration-contacts party-id]}}))

(rf/reg-event-db
  ::store-integration-contacts
  (fn [db [_ party-id _ results]]
    (let [result-map (reduce #(assoc %1 (:id %2) %2) {} results)]
      (-> (update db ::integration-contacts merge result-map)
          (assoc ::integration-contacts-loading false)
          (assoc-in [::party-integration-contact-ids party-id] (map :id results))))))

(rf/reg-event-fx
  ::get-integration-contact
  (fn [{:keys [db]} [_ app-id contact-id]]
    {:db           (merge db {::get-integration-contact-result nil
                              ::integration-contact-loading    true})
     :comm/command {:command      :party/get-integration-contact
                    :method       :get
                    :data         {:application-id      app-id
                                   :integration-user-id contact-id}
                    :on-success   [::store-integration-contact]
                    :on-not-found [::contact-not-found]}}))

(rf/reg-event-db
  ::store-integration-contact
  (fn [db [_ _ result]]
    (-> (assoc-in db [::integration-contacts (:id result)] result)
        (merge {::get-integration-contact-result (:id result)
                ::integration-contact-loading    false}))))

(rf/reg-event-db
  ::contact-not-found
  (fn [db _]
    (merge db {::get-integration-contact-result :not-found
               ::integration-contact-loading    false})))


(rf/reg-sub
  ::integration-contacts
  (fn [db [_ party-id]]
    (->> (get-in db [::party-integration-contact-ids party-id])
         (map #(get-in db [::integration-contacts %])))))

(rf/reg-sub
  ::integration-contact
  (fn [db _]
    (let [result-id (get db ::get-integration-contact-result)]
      (if (= :not-found result-id)
        result-id
        (get-in db [::integration-contacts result-id])))))

(rf/reg-sub
  ::integration-contacts-loading
  :-> ::integration-contacts-loading)

(rf/reg-sub
  ::integration-contact-loading
  :-> ::integration-contact-loading)

(rf/reg-sub
  ::integration-contact-being-created
  :-> ::integration-contact-being-created)

(defn- format-name [{:keys [organization first-name last-name]}]
  (if-not (str/blank? organization)
    (str organization (when-not (str/blank? first-name)
                        (str " (" first-name " " last-name ")")))
    (str first-name " " last-name)))

(def ^:private key-order
  [:email
   :phone
   :address
   :postal-code
   :city
   :province-state
   :country
   :bond
   :license
   :comments
   :role
   :folder-count
   :id])

(def ^:private user-key-order
  [:party/email
   :user/mobile-phone
   :user/other-phone
   :user/street-address
   :user/unit-number
   :user/lot-con
   :user/postal-code
   :user/municipality
   :user/province])

(defn- set-contact-button [tr on-click]
  [:div.flex.justify-end.mt-2
   (cc/button
     {:kind     :primary
      :size     :small
      :on-click on-click}
     (tr [:select/select]))])

(defn- contact-sheet [tr contact select-fn current-int-id backend-system]
  [:div.contact {:key (:id contact)}
   [:div.font-bold.text-md.flex.justify-between.mb-2
    (format-name contact)
    (when (:verified? contact)
      [:span.party-integration-pill.contact-verified (tr [:integration-contact/verified])])
    (when (= current-int-id (:id contact))
      [:span.party-integration-pill.contact-current (tr [:integration-contact/current-id])])]
   (->> key-order
        (keep (fn [k]
                (when-some [v (str-util/blank->nil (k contact))]
                  [:div.contact-data-row
                   [:div.contact-key (tr [(keyword "integration-contact" (name k))]
                                         [backend-system])]
                   [:div.contact-value (str v)]])))
        (cons :<>)
        vec)
   (when select-fn
     [set-contact-button tr #(select-fn (:id contact))])])

(defn- user-sheet [{:keys [tr] :as ctx} party current-int-id backend-system]
  [:div.contact
   [:div.font-bold.text-md.flex.justify-between.mb-2
    (format-name {:first-name   (party-model/get-user-key ctx party :user/first-name)
                  :last-name    (party-model/get-user-key ctx party :user/last-name)
                  :organization (party-model/get-user-key ctx party :user/corporation-or-partnership)})
    (when-not (:party/user party)
      [:span.party-integration-pill.contact-no-user (tr [:party/no-user])])]
   (->> user-key-order
        (keep (fn [k]
                (when-some [v (str-util/blank->nil (party-model/get-user-key ctx party k))]
                  [:div.contact-data-row
                   [:div.contact-key (tr [k])]
                   [:div.contact-value (str v)]])))
        (cons :<>)
        vec)
   (when current-int-id
     [:div.contact-data-row
      [:div.contact-key (tr [:integration-contact/id]
                            [backend-system])]
      [:div.contact-value current-int-id]])])

(defn- find-by-id [tr target-id select-fn backend-system]
  (r/with-let [*search-string (r/atom "")
               *last-search   (r/atom "")]
    (let [contact  (<sub [::integration-contact])
          loading? (<sub [::integration-contact-loading])]
      [:div.contact {:key (:id "find-by-id")}
       (cond
         (map? contact)
         [:<>
          [:div.font-bold.text-md.flex.justify-between.mb-2
           (format-name contact)
           (when (:verified? contact)
             [:span.contact-verified (tr [:integration-contact/verified])])]
          (->> key-order
               (keep (fn [k]
                       (when-some [v (str-util/blank->nil (k contact))]
                         [:div.contact-data-row
                          [:div.contact-key (tr [(keyword "integration-contact" (name k))])]
                          [:div.contact-value (str v)]])))
               (cons :<>)
               vec)
          (when select-fn
            [set-contact-button tr #(select-fn (:id contact))])]

         (= contact :not-found)
         [:div.font-bold.text-md
          (tr [:integration-contact/not-found]
              [backend-system @*last-search])])
       [:div.mt-3
        [:h5 (tr [:integration-contact/get-by-id]
                 [backend-system])]
        [:div.flex.w-full.content-between.items-end.mt-3
         [ui/text-input {:label-text  (tr [:integration-contact/id]
                                          [backend-system])
                         :placeholder (tr [:integration-contact/id]
                                          [backend-system])
                         :hide-label  true
                         :id          "integration-id-search"
                         :disabled    loading?
                         :value       @*search-string
                         :on-change   #(reset! *search-string (-> % .-target .-value))}]
         [:div.ml-3
          (if loading?
            (cc/inline-loading {:description (tr [:finding])})
            (cc/button
              {:kind     :tertiary
               :size     :small
               :on-click (fn [_]
                           (reset! *last-search @*search-string)
                           (>evt [::get-integration-contact target-id @*search-string]))}
              (tr [:find])))]]]])))

(defn search-integration-users-modal [{:keys [tr] :as ctx} party target *modal-open?]
  (let [party-id       (:db/id party)
        contacts       (<sub [::integration-contacts party-id])
        loading?       (<sub [::integration-contacts-loading])
        contact-by-id  (<sub [::integration-contact])
        target-id      (:db/id target)
        can-set?       (<sub [::process/signal-permitted?
                              target-id
                              [:process.signal/set-integration-contact {:party-id party-id}]])
        can-create?    (<sub [::process/signal-permitted?
                              target-id
                              [:process.signal/create-integration-contact {:party-id party-id}]])
        select-fn      (when can-set?
                         (fn [contact-id]
                           (>evt [::process/signal
                                  target-id
                                  [:process.signal/set-integration-contact {:party-id            party-id
                                                                            :integration-user-id contact-id}]])))
        customer-id    (-> target :entity/parent :domain/authority-group :group/id)
        current-int-id (or (-> party
                               :party/user
                               :user/integrations
                               (p/find-first {:user.integration/customer-id customer-id})
                               :user.integration/user-id)
                           (-> party
                               :user.integration/user-id))
        backend-system (<sub [::ws-subs/backend-system-name])]
    [ui-modal/composed-input-modal
     {:open                   @*modal-open?
      :selector-primary-focus "button.bx--modal-close"
      :on-close               (fn [_]
                                (reset! *modal-open? false)
                                true)}
     (cc/modal-header
       {:close-modal (fn [_]
                       (reset! *modal-open? false))
        :title       (tr [:party/connect-user]
                         [backend-system])})
     (cc/modal-body
       {}
       [:div.party__integration-contacts
        [:h4 (tr [:party/user-information])]
        [user-sheet ctx party current-int-id backend-system]]
       [:div.party__integration-contacts
        [:h4 (tr [:party/integration-records]
                 [backend-system])]
        (cond
          (seq contacts)
          (for-frag [c contacts]
            [contact-sheet tr c select-fn current-int-id backend-system])

          loading?
          (cc/skeleton-text {:paragraph  true
                             :line-count 10})

          (nil? contact-by-id)
          [:p.p-0 (tr [:integration-contact/no-results])])]
       [:div.party__integration-contacts
        [find-by-id tr target-id select-fn backend-system]]
       (when can-create?
         [:div.flex.justify-start.mt-6
          (if (<sub [::process/signal-processing? :process.signal/create-integration-contact])
            (cc/inline-loading {:description (tr [:creating])})
            (cc/button {:kind     :danger
                        :on-click #(>evt [::process/signal
                                          target-id
                                          [:process.signal/create-integration-contact {:party-id party-id}]])}
                       (tr [:integration-contact/create])))]))
     (cc/modal-footer
       {}
       (cc/button
         {:kind     :tertiary
          :on-click (fn [_]
                      (reset! *modal-open? false))}
         (tr [:cancel])))]))
